import ZebraBrowserPrintWrapper from "zebra-browser-print-wrapper";
import {
  formatZebraErrorForLog,
  resolveZebraError,
  type ZebraErrorContext,
} from "./zebraErrorCatalog";

const ZEBRA_API_URL = "http://localhost:9100/";
const STATUS_READ_DELAY_MS = 200;

type ZebraDevice = ReturnType<ZebraBrowserPrintWrapper["getPrinter"]>;

export class ZebraPrintApiError extends Error {
  httpStatus?: number;
  body?: string;

  constructor(message: string, context?: ZebraErrorContext) {
    super(message);
    this.name = "ZebraPrintApiError";
    this.httpStatus = context?.httpStatus;
    this.body = context?.body;
  }
}

const extractErrorContext = (error: unknown): ZebraErrorContext => {
  if (error instanceof ZebraPrintApiError) {
    return { httpStatus: error.httpStatus, body: error.body };
  }
  if (error && typeof error === "object") {
    const e = error as { httpStatus?: number; body?: string; message?: string };
    return {
      httpStatus: e.httpStatus,
      body: typeof e.body === "string" ? e.body : undefined,
    };
  }
  return {};
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const postToBrowserPrint = async (
  path: "write" | "read",
  body: unknown
): Promise<string> => {
  let response: Response;
  try {
    response = await fetch(`${ZEBRA_API_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain;charset=UTF-8",
      },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    throw new ZebraPrintApiError(
      networkError instanceof Error
        ? networkError.message
        : String(networkError),
      {
        body:
          networkError instanceof Error
            ? networkError.message
            : String(networkError),
      }
    );
  }

  const text = await response.text();
  if (!response.ok) {
    throw new ZebraPrintApiError(text || response.statusText || "Request failed", {
      httpStatus: response.status,
      body: text,
    });
  }
  return text;
};

/**
 * write() that surfaces HTTP status + body. The npm wrapper's write()
 * ignores non-OK responses, which hides connection/port failures.
 */
const writeWithResponseCapture = async (
  device: ZebraDevice,
  data: string
): Promise<void> => {
  await postToBrowserPrint("write", { device, data });
};

const readFromPrinter = async (device: ZebraDevice): Promise<string> => {
  return postToBrowserPrint("read", { device });
};

const queryPrinter = async (device: ZebraDevice, command: string): Promise<string> => {
  await writeWithResponseCapture(device, command);
  await delay(STATUS_READ_DELAY_MS);
  let result = await readFromPrinter(device);
  if (!result?.trim()) {
    await delay(STATUS_READ_DELAY_MS);
    result = await readFromPrinter(device);
  }
  return result || "";
};

const stripControlChars = (raw: string) =>
  raw.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");

/**
 * ~HQES Group 1 (rightmost 8 hex digits). Nibble 1 is the least-significant nibble.
 * Pause in ~HQES is only reported on some models (e.g. KR403).
 */
const HQES_GROUP1_ERRORS: Array<[number, string]> = [
  [0x00000001, "Paper out"],
  [0x00000002, "Ribbon Out"],
  [0x00000004, "Media Door Open"],
  [0x00000008, "Cutter Fault"],
  [0x00000010, "Printhead Overheating"],
  [0x00000020, "Motor Overheating"],
  [0x00000040, "Printhead Fault"],
  [0x00000080, "Incorrect Printhead"],
  [0x00001000, "Printer Paused"],
];

export const parseHqesStatus = (raw: string): string[] => {
  const errors: string[] = [];
  const errorsMatch = stripControlChars(raw).match(
    /ERRORS:\s*(\d)\s+([0-9A-Fa-f]+)\s+([0-9A-Fa-f]+)/i
  );
  if (!errorsMatch) return errors;

  const errorPresent = errorsMatch[1] === "1";
  const group1 = parseInt(errorsMatch[3], 16);
  if (Number.isNaN(group1)) {
    if (errorPresent) errors.push("Unknown Error");
    return errors;
  }

  HQES_GROUP1_ERRORS.forEach(([mask, message]) => {
    if (group1 & mask) errors.push(message);
  });

  if (errorPresent && errors.length === 0) {
    errors.push("Unknown Error");
  }

  return errors;
};

/**
 * ~HS string 1: aaa,b,c,... where c is pause. String 2: mmm,n,o,p,... head up / ribbon.
 */
export const parseHostStatus = (raw: string): string[] => {
  const errors: string[] = [];
  const normalized = stripControlChars(raw).trim();
  if (!normalized) return errors;

  const csvLines = normalized
    .split(/\r?\n/)
    .map((line) => {
      const match = line.match(/\d+(?:\s*,\s*\d+)+/);
      return match ? match[0].split(/\s*,\s*/) : null;
    })
    .filter((line): line is string[] => Boolean(line && line.length >= 3));

  const string1 = csvLines[0];
  if (string1) {
    if (string1[1] === "1") errors.push("Paper out");
    if (string1[2] === "1") errors.push("Printer Paused");
    if (string1[11] === "1") errors.push("Printhead Overheating");
  }

  const string2 = csvLines[1];
  if (string2) {
    if (string2[2] === "1") errors.push("Media Door Open");
    if (string2[3] === "1") errors.push("Ribbon Out");
  }

  return errors;
};

const uniqueMessages = (messages: string[]) => [...new Set(messages)];

const getPrinterHardwareErrors = async (device: ZebraDevice): Promise<string[]> => {
  const hqesRaw = await queryPrinter(device, "~HQES");
  const errors = parseHqesStatus(hqesRaw);

  let hsRaw = await queryPrinter(device, "~HS");
  if (!hsRaw.trim()) {
    hsRaw = await queryPrinter(device, '! U1 getvar "device.host_status"\r\n');
  }
  errors.push(...parseHostStatus(hsRaw));

  return uniqueMessages(errors);
};

const attachWriteCapture = (browserPrint: ZebraBrowserPrintWrapper) => {
  browserPrint.write = async (data: string) => {
    await writeWithResponseCapture(browserPrint.getPrinter(), data);
  };
};

/**
 * Print a single ZPL payload via Browser Print, capturing real API errors.
 */
export const printZebraZpl = async (zpl: string): Promise<void> => {
  const browserPrint = new ZebraBrowserPrintWrapper();
  const defaultPrinter = await browserPrint.getDefaultPrinter();
  browserPrint.setPrinter(defaultPrinter);
  attachWriteCapture(browserPrint);

  const hardwareErrors = await getPrinterHardwareErrors(browserPrint.getPrinter());
  if (hardwareErrors.length > 0) {
    const message = hardwareErrors.join(", ");
    throw new ZebraPrintApiError(message, { body: message });
  }

  await browserPrint.print(zpl);
};

export const getResolvedZebraErrorMessage = (error: unknown): string => {
  const context = extractErrorContext(error);
  const resolved = resolveZebraError(error, context);
  console.error("[Zebra Print]", formatZebraErrorForLog(resolved, context), error);
  return resolved;
};
